import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Security } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";

export function StocksSecurities(): JSX.Element {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Security | null>(null);
  const [showForm, setShowForm] = useState(false);

  const secsQ = useQuery({ queryKey: ["securities", q], queryFn: () => api.securities({ q, limit: 1000 }) });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/securities/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["securities"] }),
  });

  return (
    <>
      <PageHeader title="Securities">
        <div className="flex items-center gap-2">
          <TextInput value={q} onChange={(e) => { setQ(e.target.value); }} placeholder="Symbol / name / ISIN…" className="w-48" />
          <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add</Button>
        </div>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Class</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>ISIN</TableHead>
              <TableHead>Exchange</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {secsQ.data?.items.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.symbol}</TableCell>
                <TableCell>{s.name ?? "—"}</TableCell>
                <TableCell>{s.asset_class}</TableCell>
                <TableCell>{s.currency}</TableCell>
                <TableCell className="text-muted-foreground">{s.isin ?? "—"}</TableCell>
                <TableCell>{s.exchange ?? "—"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(s); setShowForm(true); }}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => { if (confirm(`Delete ${s.symbol}?`)) remove.mutate(s.id); }}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Modal open={showForm} onClose={() => { setShowForm(false); }} title={editing ? `Edit ${editing.symbol}` : "New security"}>
        <SecurityForm initial={editing} onDone={() => { setShowForm(false); void qc.invalidateQueries({ queryKey: ["securities"] }); }} />
      </Modal>
    </>
  );
}

function SecurityForm({ initial, onDone }: { initial: Security | null; onDone: () => void }): JSX.Element {
  const [symbol, setSymbol] = useState(initial?.symbol ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [assetClass, setAssetClass] = useState(initial?.asset_class ?? "STK");
  const [currency, setCurrency] = useState(initial?.currency ?? "USD");
  const [isin, setIsin] = useState(initial?.isin ?? "");
  const [exchange, setExchange] = useState(initial?.exchange ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        symbol,
        name: name || null,
        asset_class: assetClass,
        currency,
        isin: isin || null,
        exchange: exchange || null,
      };
      return initial ? api.update(`/securities/${initial.id}`, body) : api.create("/securities", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => { setError(e.message); },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Symbol *"><TextInput required value={symbol} onChange={(e) => { setSymbol(e.target.value.toUpperCase()); }} placeholder="AAPL" /></Field>
      <Field label="Name"><TextInput value={name} onChange={(e) => { setName(e.target.value); }} placeholder="Apple Inc." /></Field>
      <Field label="Asset class *"><TextInput required value={assetClass} onChange={(e) => { setAssetClass(e.target.value.toUpperCase()); }} placeholder="STK" /></Field>
      <Field label="Currency *"><TextInput required value={currency} onChange={(e) => { setCurrency(e.target.value.toUpperCase()); }} placeholder="USD" /></Field>
      <Field label="ISIN"><TextInput value={isin} onChange={(e) => { setIsin(e.target.value.toUpperCase()); }} placeholder="US0378331005" /></Field>
      <Field label="Exchange"><TextInput value={exchange} onChange={(e) => { setExchange(e.target.value); }} placeholder="NASDAQ" /></Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
    </form>
  );
}
